import classNames from 'classnames';
import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { OptionalStepsProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Button } from 'ui';

export interface OptionalStepsOptions {
  label: string;
  value: boolean;
}

export const OptionalSteps = ({
  title,
  options,
  subroad,
  field_name,
  moveForward,
  entity_type_prefix,
  person_details_key,
  allowSkip,
  description,
}: OptionalStepsProps) => {
  const [params, setParams] = useSearchParams();

  const step = params.get('step') ?? '0';
  const getSubStep = useCallback((step: number) => +(params.get(`sub_step${step}`) ?? 0), [params]);
  const setSubStep = useCallback(
    (step: number, value: number) => params.set(`sub_step${step}`, String(value)),
    [params],
  );
  const subStep = getSubStep(+step);
  const [currentSubStep, setCurrentSubStep] = useState(subStep && !isNaN(+subStep) ? +subStep : 0);

  useEffect(() => {
    setCurrentSubStep(subStep && !isNaN(+subStep) ? +subStep : 0);
  }, [subStep]);

  const { id: entity_id = 0 } = useParams();

  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const handleNextSubStep = useCallback(() => {
    const newStep = currentSubStep + 1;
    setSubStep(+step, newStep);
    setCurrentSubStep(newStep);
    setParams(params, { replace: false });
  }, [currentSubStep, setSubStep, step, setParams, params]);

  const initialValue = () => {
    return {
      [entity_type_prefix]: {
        [field_name]: data?.entity[entity_type_prefix]?.[field_name],
      },
    };
  };

  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    enableReinitialize: true,
    validationSchema: yup.object({
      [entity_type_prefix]: yup.object({
        [field_name]: yup.boolean().required('Please choose option'),
      }),
    }),
    initialValues: initialValue(),
    onSubmit: async values => {
      const isValuesInDataActual = isEqual(
        values?.[entity_type_prefix]?.[field_name],
        data?.entity?.[entity_type_prefix]?.[field_name],
      );

      if (formik.dirty || !isValuesInDataActual) {
        await updateEntity({
          ...data?.entity,
          [entity_type_prefix]: {
            ...data?.entity?.[entity_type_prefix as keyof typeof data.entity],
            ...values?.[entity_type_prefix],
          },
        });
      }
      values?.[entity_type_prefix]?.[field_name]
        ? handleNextSubStep()
        : moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeOption = (value: boolean) => {
    formik.setFieldValue(`${entity_type_prefix}.${field_name}`, value);
    formik.setFieldError(`${entity_type_prefix}.${field_name}`, '');
  };
  const renderSubroadStep = (step: number) => {
    const CurrentStep = subroad?.[step - 1];

    if (currentSubStep === 0 || !CurrentStep) {
      return <></>;
    }

    return (
      <CurrentStep
        entity_type_prefix={entity_type_prefix}
        person_details_key={person_details_key}
        moveForward={() =>
          subroad.length === step ? moveForward && moveForward() : handleNextSubStep()
        }
        allowSkip={allowSkip}
      />
    );
  };

  if (currentSubStep > 0) {
    return renderSubroadStep(currentSubStep);
  }
  return (
    <OnboardingWrapper
      title={title}
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={formik.isSubmitting}
      formik={formik}
      className="max-w-[560px] w-full"
    >
      {description && <span className="text-sm leading-[22px] font-[Inter]">{description}</span>}
      <div className="w-full flex flex-col gap-2">
        {options.map(({ label, value }, i) => (
          <Button
            key={'ButtonOptions_' + i}
            onClick={() => handleChangeOption(value)}
            variant="secondary"
            className={classNames('capitalize', {
              'border-[#486a7b]': formik.values[entity_type_prefix]?.[field_name] === value,
            })}
            textAlign="left"
          >
            {label}
          </Button>
        ))}
        {formik.errors?.[entity_type_prefix]?.[field_name] && (
          <span className="text-error-red text-sm">
            {formik.errors?.[entity_type_prefix]?.[field_name] as unknown as string}
          </span>
        )}
      </div>
    </OnboardingWrapper>
  );
};
